import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getPartnerDisplayName } from '../common/partnerUtils';

const setMetaTag = (attr, key, value) => {
  if (!value) return;
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
};

const setLinkTag = (rel, href) => {
  if (!href) return;
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

export default function PartnerMeta({ partner, title, description, image, disableFavicon }) {
  const settings = useSelector((state) => state.settingsdata.settings);
  const prevTitle = useRef('');
  const prevFavicon = useRef('');

  useEffect(() => {
    if (!partner || typeof document === 'undefined') return undefined;

    const partnerName = getPartnerDisplayName(partner, settings) || 'Partner';
    const appName = settings?.CompanyName || 'Plukk';
    const pageTitle =
      title ||
      partner.pageTitle ||
      partner.metaTitle ||
      partner.seoTitle ||
      `${partnerName} | ${appName}`;
    const metaDescription =
      description ||
      partner.metaDescription ||
      partner.seoDescription ||
      partner.description ||
      partner.tagline ||
      '';
    const metaImage = image || partner.ogImage || partner.socialImage || partner.logoUrl || '';
    const favicon = partner.faviconUrl || partner.logoUrl || partner.logo || '';

    prevTitle.current = document.title;
    document.title = pageTitle;

    setMetaTag('name', 'description', metaDescription);
    setMetaTag('property', 'og:title', pageTitle);
    setMetaTag('property', 'og:description', metaDescription);
    if (metaImage) {
      setMetaTag('property', 'og:image', metaImage);
    }
    setMetaTag('name', 'twitter:card', metaImage ? 'summary_large_image' : 'summary');
    setMetaTag('name', 'twitter:title', pageTitle);
    setMetaTag('name', 'twitter:description', metaDescription);
    if (metaImage) {
      setMetaTag('name', 'twitter:image', metaImage);
    }

    if (!disableFavicon && favicon) {
      const currentIcon = document.querySelector('link[rel="icon"]');
      prevFavicon.current = currentIcon ? currentIcon.getAttribute('href') || '' : '';
      setLinkTag('icon', favicon);
      setLinkTag('shortcut icon', favicon);
      setLinkTag('apple-touch-icon', favicon);
    }

    return () => {
      if (prevTitle.current) {
        document.title = prevTitle.current;
      }
      if (!disableFavicon && prevFavicon.current) {
        setLinkTag('icon', prevFavicon.current);
        setLinkTag('shortcut icon', prevFavicon.current);
        setLinkTag('apple-touch-icon', prevFavicon.current);
      }
    };
  }, [partner, title, description, image, settings, disableFavicon]);

  return null;
}
